/* import __COLOCATED_TEMPLATE__ from './side-by-side-review.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { taskFor } from 'ember-concurrency-ts';
import type ContentSectionRelationship from 'embercom/models/content-service/content-section-relationship';
import type SuggestionService from 'embercom/services/suggestion-service';
import type Store from '@ember-data/store';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import type { EntityIdentifierTuple } from 'embercom/services/content-suggestions-service';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import { EntityType } from 'embercom/models/data/entity-types';

export interface Args {
  activeContent: ContentSectionRelationship;
}

interface Signature {
  Args: Args;
  Element: HTMLElement;
}

export default class SideBySideReview extends Component<Signature> {
  @service declare suggestionService: SuggestionService;
  @service declare store: Store;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;

  get reviewRequestCreatedAt() {
    if (this.args.activeContent.contentReviewRequest) {
      return this.args.activeContent.contentReviewRequest.get('createdAt');
    }
    return '';
  }

  @action
  async handleSuggestion(action: 'accept' | 'reject') {
    let reviewRequest = this.store.peekRecord(
      'content-service/content-review-request',
      this.args.activeContent.contentReviewRequest.get('id'),
    );
    let nextContent = this.contentSuggestionsService.getNextSuggestionFromList({
      entityId: parseInt(this.args.activeContent.id, 10),
      entityType: this.args.activeContent.entityType,
    });
    let currentSuggestionId = this.args.activeContent.id;
    let currentEntityType = this.args.activeContent.entityType;

    if (nextContent) {
      this.openContentInReviewMode(nextContent);
    } else {
      // Close the drawer editor if no other suggestion exists. In focus mode this
      // will reset the drawer editor query params which will show the empty state instead
      await this.knowledgeHubDrawerEditorService.closeDrawer();
    }

    if (action === 'accept') {
      await taskFor(this.suggestionService.approveSuggestion).perform(
        reviewRequest,
        EntityType.ContentSectionRelationship,
      );
    } else if (action === 'reject') {
      await taskFor(this.suggestionService.rejectSuggestion).perform(reviewRequest);
    }

    this.contentSuggestionsService.removeSuggestionFromList({
      entity_id: parseInt(currentSuggestionId, 10),
      entity_type: currentEntityType,
    });
  }

  private openContentInReviewMode(content: EntityIdentifierTuple) {
    this.knowledgeHubDrawerEditorService.openReviewDrawer({
      activeContentId: content.entityId,
      activeContentType: objectNames[content.entityType],
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::SideBySideReview': typeof SideBySideReview;
    'knowledge-hub/content-editor/side-by-side-review': typeof SideBySideReview;
  }
}
